import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toSupportedLanguage } from '@/i18n';

const languages = [
  { code: 'pt', label: 'PT', flag: '🇧🇷', name: 'Português' },
  { code: 'en', label: 'EN', flag: '🇺🇸', name: 'English' },
  { code: 'es', label: 'ES', flag: '🇪🇸', name: 'Español' },
  { code: 'fr', label: 'FR', flag: '🇫🇷', name: 'Français' },
];

export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const activeCode = toSupportedLanguage(i18n.resolvedLanguage || i18n.language);
  const current = languages.find((l) => l.code === activeCode) || languages[0];

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        className="nav-icon-btn flex items-center gap-1 min-h-11 px-2.5 rounded-full font-display text-xs font-semibold whitespace-nowrap"
        aria-label={`${t('nav.language')}: ${current.name}`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="text-sm">{current.flag}</span>
        <span className="hidden sm:inline">{current.label}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-40 bg-white rounded-2xl shadow-xl border border-reef/15 overflow-hidden z-50 animate-fade-slide-up p-1">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setOpen(false);
              }}
              lang={lang.code === 'pt' ? 'pt-BR' : lang.code}
              aria-current={current.code === lang.code ? 'true' : undefined}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm transition-colors hover:bg-reef/5 ${
                current.code === lang.code ? 'text-reef font-semibold bg-reef/5' : 'text-pearl/70'
              }`}
            >
              <span className="text-base" aria-hidden="true">{lang.flag}</span>
              <span>{lang.name}</span>
              {current.code === lang.code && (
                <i className="ri-check-line ml-auto text-reef" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}